import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { EScheduledPromptTypes } from 'types/copilot';

import { buildMeetingAskAvomaAbsoluteUrl } from 'helpers/meetingAskAvomaNavigation';

import {
  CHAT_CONTEXT_TYPES,
  CHAT_CONTEXT_TYPE_NAMES,
  MESSAGE_AUTHOR_TYPES,
  SCHEDULED_FOR_YOU,
  SCHEDULED_PROMPT_STATE_TYPES
} from 'constants/copilot';
import { DATE_FORMATS } from 'constants/dateFormats';
import {
  BULLET_POINT_REGEX,
  DASHES_REGEX,
  DEAL_COPILOT_CITATION_REGEX,
  DOUBLE_ASTERISKS_REGEX,
  HEADER_SEPARATOR_LINE_REGEX,
  HTML_TAGS_REGEX,
  MEETING_ENGAGEMENT_REGEX,
  REMAINING_PIPES_REGEX,
  TABLE_CELL_REGEX,
  TIMESTAMP_REGEX,
  TRIPLE_PIPE_REGEX
} from 'constants/regex';

import { minutesSeconds } from './getPrettyTimeFromSeconds';

/**
 * Copilot chat APIs sometimes return `context_type` as a string; normalize so comparisons
 * to {@link CHAT_CONTEXT_TYPES} stay reliable (e.g. meeting history → CRM context).
 */
export const normalizeCopilotContextType = contextType => {
  if (contextType === null || contextType === undefined) {
    return CHAT_CONTEXT_TYPES.GLOBAL;
  }
  if (typeof contextType === 'number' && !Number.isNaN(contextType)) {
    return contextType;
  }
  if (typeof contextType === 'string') {
    const trimmed = contextType.trim();
    if (trimmed !== '') {
      const asNum = Number(trimmed);
      if (!Number.isNaN(asNum)) {
        return asNum;
      }
      switch (trimmed.toLowerCase()) {
        case 'meeting':
        case 'call':
          return CHAT_CONTEXT_TYPES.MEETING;
        case 'deal':
        case 'opportunity':
          return CHAT_CONTEXT_TYPES.DEAL;
        case 'company':
        case 'account':
          return CHAT_CONTEXT_TYPES.COMPANY;
        case 'global':
          return CHAT_CONTEXT_TYPES.GLOBAL;
        case 'scheduled':
          return CHAT_CONTEXT_TYPES.SCHEDULED;
        default:
          break;
      }
    }
  }
  return CHAT_CONTEXT_TYPES.GLOBAL;
};

/**
 * Merges in-memory thread messages with `GET /api/v1/copilot/chats/:uuid` detail `messages`.
 * The server can return before the first user turn is fully reflected, or return only
 * assistant rows, which would clobber the optimistic `uuid: null` user message from
 * `SEND_COPILOT_MESSAGE` if we naively spread `chat` over `prev`.
 */
export const mergeCopilotChatDetailMessages = (prev, incoming) => {
  const prevArr = Array.isArray(prev) ? prev : [];
  const inc = Array.isArray(incoming) ? incoming : [];

  if (inc.length === 0) {
    return prevArr;
  }

  const incomingHasUser = inc.some(
    m => m?.author === MESSAGE_AUTHOR_TYPES.USER
  );
  if (!incomingHasUser) {
    const optimisticUserRows = prevArr.filter(
      m =>
        m?.author === MESSAGE_AUTHOR_TYPES.USER &&
        (m?.uuid == null || m?.uuid === '')
    );
    if (optimisticUserRows.length > 0) {
      return [...optimisticUserRows, ...inc];
    }
  }

  if (incomingHasUser) {
    const optimisticWithPills = prevArr.find(
      m =>
        m?.author === MESSAGE_AUTHOR_TYPES.USER &&
        (m?.uuid == null || m?.uuid === '') &&
        Array.isArray(m.copilotStandaloneContextPills) &&
        m.copilotStandaloneContextPills.length > 0
    );
    if (optimisticWithPills) {
      const optMsg = optimisticWithPills.message || '';
      const pills = optimisticWithPills.copilotStandaloneContextPills;
      return inc.map(m => {
        if (
          m?.author === MESSAGE_AUTHOR_TYPES.USER &&
          (m?.message || '') === optMsg &&
          (!m.copilotStandaloneContextPills ||
            m.copilotStandaloneContextPills.length === 0)
        ) {
          return { ...m, copilotStandaloneContextPills: pills };
        }
        return m;
      });
    }
  }

  return inc;
};

export const getContextTypeLabel = type => {
  const t = normalizeCopilotContextType(type);
  if (t === CHAT_CONTEXT_TYPES.MEETING) {
    return CHAT_CONTEXT_TYPE_NAMES.MEETING;
  }

  if (t === CHAT_CONTEXT_TYPES.COMPANY) {
    return CHAT_CONTEXT_TYPE_NAMES.COMPANY;
  }

  if (t === CHAT_CONTEXT_TYPES.DEAL) {
    return CHAT_CONTEXT_TYPE_NAMES.DEAL;
  }

  if (t === CHAT_CONTEXT_TYPES.GLOBAL) {
    return CHAT_CONTEXT_TYPE_NAMES.GLOBAL;
  }

  return '';
};

// --- Copilot WebSocket stream `data_json.context_uuids` (see `helpers/websockets`)

const PRIMARY_CONTEXT_TYPE_TO_CONTEXT_UUIDS_KEY = {
  [CHAT_CONTEXT_TYPES.MEETING]: 'meeting_uuids',
  [CHAT_CONTEXT_TYPES.CALL]: 'meeting_uuids',
  [CHAT_CONTEXT_TYPES.DEAL]: 'deal_uuids',
  [CHAT_CONTEXT_TYPES.COMPANY]: 'account_uuids'
};

/** @-mention categories that still map to `data_json.context_uuids` (members/teams use `data_json.mentions.member_emails` / `team_uuids`). */
const MENTION_CATEGORY_KEY_TO_CONTEXT_UUIDS_KEY = {
  meetings: 'meeting_uuids',
  companies: 'account_uuids',
  deals: 'deal_uuids',
  playlists: 'playlist_uuids'
};

function pushUniqueStringToBucket(bucket, value) {
  if (bucket.includes(value)) {
    return;
  }
  bucket.push(value);
}

/**
 * Builds `data_json.context_uuids` and optional `data_json.mentions` for the Copilot
 * WebSocket send payload: context buckets from primary scope + @-pills, with members and
 * teams split out into `mentions.member_emails` and `mentions.team_uuids`.
 * @param {number | null | undefined} contextType
 * @param {string | null | undefined} contextUuid
 * @param {Array<{ id: string, categoryKey: string, email?: string }> | null | undefined} additionalContextMentions
 * @returns {{ context_uuids?: Record<string, string[]>, mentions?: { member_emails: string[], team_uuids: string[] } } | undefined}
 */
export function getCopilotStreamDataJsonContextUuids(
  contextType,
  contextUuid,
  additionalContextMentions
) {
  const context_uuids = {};
  const mentions = { member_emails: [], team_uuids: [] };

  const primaryKey = PRIMARY_CONTEXT_TYPE_TO_CONTEXT_UUIDS_KEY[contextType];
  if (primaryKey && contextUuid) {
    if (!context_uuids[primaryKey]) {
      context_uuids[primaryKey] = [];
    }
    pushUniqueStringToBucket(context_uuids[primaryKey], contextUuid);
  }

  for (const entry of additionalContextMentions || []) {
    const id = entry?.id;
    if (typeof id === 'string' && id) {
      const cat = entry?.categoryKey;
      if (cat === 'members') {
        const fromField =
          typeof entry.email === 'string' && entry.email.trim() !== ''
            ? entry.email.trim()
            : null;
        const fromId = id.includes('@') ? id : null;
        const email = fromField || fromId;
        if (email) {
          pushUniqueStringToBucket(mentions.member_emails, email);
        }
      } else if (cat === 'teams') {
        pushUniqueStringToBucket(mentions.team_uuids, id);
      } else {
        const wireKey = cat && MENTION_CATEGORY_KEY_TO_CONTEXT_UUIDS_KEY[cat];
        if (wireKey) {
          if (!context_uuids[wireKey]) {
            context_uuids[wireKey] = [];
          }
          pushUniqueStringToBucket(context_uuids[wireKey], id);
        }
      }
    }
  }

  const hasContextUuids = Object.keys(context_uuids).length > 0;
  const hasMentions =
    mentions.member_emails.length > 0 || mentions.team_uuids.length > 0;
  if (!hasContextUuids && !hasMentions) {
    return undefined;
  }
  const out = {};
  if (hasContextUuids) {
    out.context_uuids = context_uuids;
  }
  if (hasMentions) {
    out.mentions = {
      member_emails: mentions.member_emails,
      team_uuids: mentions.team_uuids
    };
  }
  return out;
}

/**
 * Derives scheduled prompts (active/paused) from saved prompts data.
 * Use this instead of calling getCopilotScheduledPrompts API when you already have getCopilotSavedPrompts data.
 * @param {Record<string, object>|undefined} savedPromptsData - Map of prompt uuid to prompt (from getCopilotSavedPrompts)
 * @param {number[]} [stateFilter] - Schedule states to include (default: ACTIVE, PAUSED)
 * @returns {object[]} Array of prompts that have at least one schedule in the given states
 */
export const getScheduledPromptsFromSavedPrompts = (
  savedPromptsData,
  stateFilter = [
    SCHEDULED_PROMPT_STATE_TYPES.ACTIVE,
    SCHEDULED_PROMPT_STATE_TYPES.PAUSED
  ]
) => {
  if (!savedPromptsData || typeof savedPromptsData !== 'object') {
    return [];
  }
  return Object.values(savedPromptsData).filter(prompt =>
    prompt?.scheduledPrompts?.some(s => stateFilter.includes(s?.state))
  );
};

export const formatUserMarkdownMessage = message =>
  message ? message.replace(/\n/g, '  \n') : '';

export const formatMeetingChatMarkdownMessage = message =>
  message
    ? message.replace(TRIPLE_PIPE_REGEX, '\n\n $1').replace(/\n/g, '  \n')
    : '';

export const formatDealChatMarkdownMessage = message =>
  message
    ? message
        .replace(DEAL_COPILOT_CITATION_REGEX, '>\n\n')
        .replace(/\n/g, '  \n')
    : '';

export const formatSessionTimestamp = timestamp => {
  const date = moment(timestamp);

  return date.isSame(moment(), 'day')
    ? `Today, ${date.format(DATE_FORMATS.SHORT_MONTH_DAY)}`
    : date.format(DATE_FORMATS.DAY_OF_WEEK_SHORT_MONTH_DAY);
};

export const convertMarkdownToPlainText = markdown =>
  // Trim leading and trailing whitespace
  markdown
    .replace(/<(?:M\d+(?:\.\d+)?(?:\+\d+)?|E\d+(?:\.\d+)?|\d+(?:\.\d+)?)>/g, '') // Remove all citation markers in a single pass

    .replace(HEADER_SEPARATOR_LINE_REGEX, '') // Remove header separator lines
    .replace(TABLE_CELL_REGEX, '$1') // Remove table cells with pipes
    .replace(REMAINING_PIPES_REGEX, '') // Remove remaining pipes

    .replace(DASHES_REGEX, '') // Remove dashes if there are 2 or more in a row
    .replace(BULLET_POINT_REGEX, '- ') // Replace bullet points with dashes
    .replace(DOUBLE_ASTERISKS_REGEX, '') // Remove double asterisks
    .trim();

export const convertTableElementToPlainText = tableElement => {
  const result = [];

  // Get all rows (including header rows)
  const allRows = tableElement.querySelectorAll('tr');

  if (allRows.length === 0) {
    return [];
  }

  // Iterate through each row (top to bottom)
  allRows.forEach((row, rowIndex) => {
    const cells = row.querySelectorAll('th, td');

    // Iterate through each cell in the row (left to right)
    cells.forEach(cell => {
      let cellContent = '';

      // Check if cell contains links
      const linkElements = cell.querySelectorAll('a');

      if (linkElements.length > 0) {
        // If there are links, we need to preserve both text and links
        // Create a temporary container to work with the cell content
        const tempContainer = document.createElement('div');

        tempContainer.innerHTML = cell.innerHTML;

        // Replace all link elements with their markdown format
        const tempLinks = tempContainer.querySelectorAll('a');

        tempLinks.forEach(linkElement => {
          const linkText = linkElement.textContent?.trim() || '';
          const linkHref = linkElement.getAttribute('href') || '';
          const markdownLink = `[${linkText}](${linkHref})`;

          // Replace the link element with its markdown representation
          linkElement.outerHTML = markdownLink;
        });

        // Get the text content after replacing links
        cellContent = tempContainer.textContent?.trim() || '';
      } else {
        // Otherwise, just get the text content
        cellContent = cell.textContent?.trim() || '';
      }

      // Check if this is a header cell (th element)
      if (cell.tagName.toLowerCase() === 'th') {
        // Wrap header text with bold markers
        result.push(`**${cellContent}**`);
      } else {
        result.push(cellContent);
      }
    });

    // Add line break after each row (except the last row)
    if (rowIndex < allRows.length - 1) {
      result.push('\n\n');
    }
  });

  return result;
};

export const formatMarkdownWithCitationsAsLinks = ({
  shouldIncludeTimestamps,
  markdown,
  meetingUuid,
  contextMap,
  copilotSessionUuid
}) => {
  let processedMarkdown = markdown;

  if (!shouldIncludeTimestamps) {
    // Trim trailing spaces before the tag
    processedMarkdown = processedMarkdown.replace(/\s+(?=<[^>]+>)/g, '');

    // Remove all citation markers in a single pass
    processedMarkdown = processedMarkdown.replace(
      /<(?:M\d+(?:\.\d+)?(?:\+\d+)?|E\d+(?:\.\d+)?|\d+(?:\.\d+)?)>/g,
      ''
    );
  }

  return processedMarkdown.replace(HTML_TAGS_REGEX, match => {
    const formatMarkdownLink = (id, url) => `[${minutesSeconds(id)}](${url})`;
    // Ask Avoma tab — transcript deep links assume the meeting page; unified history is app-wide.
    const formatTimestampLink = uuid =>
      buildMeetingAskAvomaAbsoluteUrl(uuid, copilotSessionUuid);

    const isTimestampCitation = TIMESTAMP_REGEX.test(match);
    const isMeetingEngagementCitation = MEETING_ENGAGEMENT_REGEX.test(match);

    if (isTimestampCitation && meetingUuid) {
      const timestamp = match.replace(/[<>]/g, '');
      const link = formatTimestampLink(meetingUuid);
      return formatMarkdownLink(timestamp, link);
    }

    if (isMeetingEngagementCitation && !isEmpty(contextMap)) {
      const meetingEngagementId = match.replace(/[<>]/g, '');
      const meetingEngagement = contextMap[meetingEngagementId];

      if (meetingEngagement) {
        const { uuid: meetingEngagementUuid, metadata: { timestamp } = {} } =
          meetingEngagement;
        const link = formatTimestampLink(meetingEngagementUuid);
        return formatMarkdownLink(timestamp, link);
      }
    }

    // Return the original match if it's not a citation we want to transform
    return match;
  });
};

export const truncateChatName = (text, maxLength = 100) => {
  if (text.length > maxLength) {
    // Subtract 3 to account for the ellipsis and slice the text
    return `${text.slice(0, maxLength - 3)}...`;
  }

  return text;
};

export const groupChatsByDate = chats => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentYear = today.getFullYear();

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const lastWeekStart = new Date(today);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);

  const lastMonthStart = new Date(today);
  lastMonthStart.setDate(lastMonthStart.getDate() - 30);

  // First, group the chats by category
  const groupedChats = chats.reduce((acc, chat) => {
    const chatDate = new Date(chat.created);
    chatDate.setHours(0, 0, 0, 0);

    let category = '';

    if (chatDate.getTime() === today.getTime()) {
      category = 'Today';
    } else if (chatDate.getTime() === yesterday.getTime()) {
      category = 'Yesterday';
    } else if (chatDate >= lastWeekStart) {
      category = 'Last 7 Days';
    } else if (chatDate >= lastMonthStart) {
      category = 'Last 30 Days';
    } else {
      // Only show year if it's different from current year
      const chatYear = chatDate.getFullYear();
      category = chatDate.toLocaleDateString('en-US', {
        month: 'long',
        ...(chatYear !== currentYear && { year: 'numeric' })
      });
    }

    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(chat);
    return acc;
  }, {});

  // Sort chats within each category by date (newest first)
  Object.keys(groupedChats).forEach(category => {
    groupedChats[category].sort(
      (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
    );
  });

  // Return an ordered object with categories in chronological order
  const orderedCategories = [
    'Today',
    'Yesterday',
    'Last 7 Days',
    'Last 30 Days'
  ];
  const result = {};

  // Add the fixed categories first
  orderedCategories.forEach(category => {
    if (groupedChats[category]?.length) {
      result[category] = groupedChats[category];
    }
  });

  // Add the remaining month-year categories, sorted newest to oldest
  Object.keys(groupedChats)
    .filter(category => !orderedCategories.includes(category))
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
    .forEach(category => {
      result[category] = groupedChats[category];
    });

  return result;
};

const pickFirstNonEmptyStringKey = (entity, keys) => {
  if (!entity || typeof entity !== 'object') {
    return null;
  }
  for (const key of keys) {
    const v = entity[key];
    if (typeof v === 'string' && v.trim() !== '') {
      return v.trim();
    }
    if (typeof v === 'number' && !Number.isNaN(v)) {
      return String(v);
    }
  }
  return null;
};

/** CRM payloads / meeting tags use varying keys for human-readable names. */
const COPILOT_CRM_DEAL_NAME_KEYS = [
  'name',
  'title',
  'opportunityName',
  'opportunity_name',
  'dealName',
  'deal_name',
  'displayName',
  'display_name'
];

const COPILOT_CRM_COMPANY_NAME_KEYS = [
  'name',
  'accountName',
  'account_name',
  'companyName',
  'company_name',
  'displayName',
  'display_name',
  'commonName',
  'legalName'
];

/**
 * @param {Record<string, unknown> | null | undefined} deal
 * @returns {string | null}
 */
export const pickCopilotCrmDealDisplayName = deal =>
  pickFirstNonEmptyStringKey(deal, COPILOT_CRM_DEAL_NAME_KEYS);

/**
 * @param {Record<string, unknown> | null | undefined} company
 * @returns {string | null}
 */
export const pickCopilotCrmCompanyDisplayName = company =>
  pickFirstNonEmptyStringKey(company, COPILOT_CRM_COMPANY_NAME_KEYS);

const FALLBACK_DEAL_DROPDOWN_LABEL = 'Deal';
const FALLBACK_COMPANY_DROPDOWN_LABEL = 'Company';

export const getChatDropdownLabel = ({
  contextType,
  meeting,
  company,
  deal
}) => {
  const t = normalizeCopilotContextType(contextType);
  if (t === CHAT_CONTEXT_TYPES.MEETING) {
    return `This ${meeting?.isCall ? 'call' : 'meeting'}`;
  }

  if (t === CHAT_CONTEXT_TYPES.COMPANY) {
    return (
      pickCopilotCrmCompanyDisplayName(company) ||
      FALLBACK_COMPANY_DROPDOWN_LABEL
    );
  }

  if (t === CHAT_CONTEXT_TYPES.DEAL) {
    return pickCopilotCrmDealDisplayName(deal) || FALLBACK_DEAL_DROPDOWN_LABEL;
  }

  return 'Anything';
};

export const getNewChatContextObj = ({
  contextUuid,
  contextType,
  meeting,
  company,
  deal
}) => {
  const normalizedType = normalizeCopilotContextType(
    contextType ?? CHAT_CONTEXT_TYPES.GLOBAL
  );
  const getChatTitle = () => {
    if (normalizedType === CHAT_CONTEXT_TYPES.MEETING) {
      return `Ask Avoma about ${meeting?.subject || 'this meeting'}`;
    }

    if (normalizedType === CHAT_CONTEXT_TYPES.COMPANY) {
      return (
        pickCopilotCrmCompanyDisplayName(company) ||
        FALLBACK_COMPANY_DROPDOWN_LABEL
      );
    }

    if (normalizedType === CHAT_CONTEXT_TYPES.DEAL) {
      return pickCopilotCrmDealDisplayName(deal) || FALLBACK_DEAL_DROPDOWN_LABEL;
    }

    return 'Ask Avoma about your conversations, deals, or companies...';
  };

  return {
    uuid: null,
    name: getChatTitle(),
    contextUuid: contextUuid ?? null,
    contextType: normalizedType,
    label: getChatDropdownLabel({
      contextType: normalizedType,
      meeting,
      company,
      deal
    })
  };
};

export const formatTime = seconds => {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;

  const mins = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(1);

  return `${mins}:${secs.padStart(4, '0')}`;
};

export const formatStepTime = ({
  timestamp,
  nextTimestamp,
  startTime,
  thinkingMessagesLength,
  isActive,
  endTime,
  capturedEndTime
}) => {
  if (!startTime) return '0.0s';

  const stepStart = new Date(timestamp).getTime();

  // For a single step that's completed, show the total elapsed time
  if (thinkingMessagesLength === 1 && !isActive) {
    const start = new Date(startTime).getTime();

    let end;

    if (endTime) {
      end = new Date(endTime).getTime();
    } else if (capturedEndTime !== null) {
      end = capturedEndTime;
    } else {
      // Fallback to step timestamp + buffer
      end = stepStart + 200;
    }
    let totalElapsed = Math.floor((end - start) / 100) / 10;
    // Ensure minimum duration of 0.1s for completed single steps
    if (totalElapsed <= 0) {
      totalElapsed = 0.1;
    }
    return formatTime(totalElapsed);
  }

  // Calculate duration from this step to the next step (or end time)
  let stepEnd;

  if (nextTimestamp) {
    stepEnd = new Date(nextTimestamp).getTime();
  } else if (endTime) {
    stepEnd = new Date(endTime).getTime();
  } else if (capturedEndTime !== null) {
    stepEnd = capturedEndTime;
  } else {
    // If no next timestamp and still active, return 0
    return formatTime(0);
  }

  let stepDuration = Math.floor((stepEnd - stepStart) / 100) / 10; // Convert to seconds with 1 decimal

  // Ensure minimum duration of 0.1s for completed steps
  if (!isActive && stepDuration <= 0) {
    stepDuration = 0.1;
  }

  return formatTime(stepDuration);
};

/**
 * Generates an RFC5545 compliant RRULE string with DTSTART for scheduled prompts
 * @param {Object} params
 * @param {string} params.frequency - 'daily' | 'weekly' | 'monthly' | 'weekdays'
 * @param {string} [params.dayOfWeek] - Day of week for weekly frequency (e.g., 'monday')
 * @param {string} [params.dayOfMonth] - Day of month for monthly frequency (e.g., '15')
 * @param {string} params.time - Time in 24h format (e.g., '09:00')
 * @param {string} [params.timezone] - IANA timezone (defaults to user's timezone)
 * @param {Date} [params.startDate] - Start date (defaults to today)
 * @returns {string} RFC5545 formatted string with DTSTART and RRULE
 */
export const generateScheduledPromptRRule = ({
  frequency,
  dayOfWeek,
  dayOfMonth,
  time,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  startDate = new Date()
}) => {
  // Format DTSTART date: YYYYMMDDTHHMMSS
  const [hours, minutes] = time.split(':');
  const year = startDate.getFullYear();
  const month = String(startDate.getMonth() + 1).padStart(2, '0');
  const day = String(startDate.getDate()).padStart(2, '0');
  const dtstart = `DTSTART;TZID=${timezone}:${year}${month}${day}T${hours}${minutes}00`;

  // Build RRULE based on frequency
  let rrule = 'RRULE:';

  switch (frequency) {
    case 'daily':
      rrule += 'FREQ=DAILY';
      break;

    case 'weekly':
      rrule += `FREQ=WEEKLY;BYDAY=${dayOfWeek || 'MO'}`;
      break;

    case 'monthly':
      rrule += `FREQ=MONTHLY;BYMONTHDAY=${dayOfMonth || '1'}`;
      break;

    case 'weekdays':
      rrule += 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR';
      break;

    default:
      rrule += 'FREQ=DAILY';
  }

  return `${dtstart}\n${rrule}`;
};

/**
 * Parses an RFC5545 RRULE string back into component-friendly values
 * @param {string} rruleString - The RRULE string (e.g., "DTSTART;TZID=America/Los_Angeles:20251204T090000\nRRULE:FREQ=MONTHLY;BYMONTHDAY=31")
 * @returns {Object} Parsed values for form fields
 */
export const parseScheduledPromptRRule = rruleString => {
  const result = {
    frequency: 'daily',
    dayOfWeek: 'MO',
    dayOfMonth: '1',
    time: '09:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };

  if (!rruleString) return result;

  // Parse DTSTART for time and timezone
  const dtstartMatch = rruleString.match(
    /DTSTART;TZID=([^:]+):(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/
  );
  if (dtstartMatch) {
    const [, timezone, , , , hours, minutes] = dtstartMatch;
    result.timezone = timezone;
    result.time = `${hours}:${minutes}`;
  }

  // Parse RRULE for frequency and other params
  const rruleMatch = rruleString.match(/RRULE:(.+)/);
  if (!rruleMatch) return result;

  const rrulePart = rruleMatch[1];
  const params = {};

  // Parse RRULE params into key-value pairs
  rrulePart.split(';').forEach(param => {
    const [key, value] = param.split('=');
    params[key] = value;
  });

  // Determine frequency
  if (params.FREQ === 'DAILY') {
    result.frequency = 'daily';
  } else if (params.FREQ === 'WEEKLY') {
    // Check if it's weekdays (MO,TU,WE,TH,FR) or single day
    if (params.BYDAY === 'MO,TU,WE,TH,FR') {
      result.frequency = 'weekdays';
    } else {
      result.frequency = 'weekly';
      result.dayOfWeek = params.BYDAY || 'MO';
    }
  } else if (params.FREQ === 'MONTHLY') {
    result.frequency = 'monthly';
    result.dayOfMonth = params.BYMONTHDAY || '1';
  } else {
    // Default to daily for unknown frequency
    result.frequency = 'daily';
  }

  return result;
};

const DAY_NAMES = {
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
  SU: 'Sunday'
};

/**
 * Formats a 24-hour time string to 12-hour format with AM/PM
 * @param {string} time - Time in "HH:MM" format (e.g., "09:00", "14:30")
 * @returns {string} Formatted time (e.g., "9:00 AM", "2:30 PM")
 */
const formatTimeTo12Hour = time => {
  if (!time) return '';

  const [hoursStr, minutes] = time.split(':');
  const hours = parseInt(hoursStr, 10);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;

  return `${hours12}:${minutes} ${period}`;
};

/**
 * Formats an RRULE string into a human-readable schedule description
 * @param {string} rruleString - The RRULE string to format
 * @returns {string} Human-readable schedule description (e.g., "Daily at 9:00 AM", "Weekly on Mondays at 9:00 AM")
 */
export const formatRruleToHumanReadable = rruleString => {
  if (!rruleString) return '';

  const parsed = parseScheduledPromptRRule(rruleString);
  const timeStr = formatTimeTo12Hour(parsed.time);

  switch (parsed.frequency) {
    case 'daily':
      return `Daily at ${timeStr}`;

    case 'weekly': {
      const dayName = DAY_NAMES[parsed.dayOfWeek] || 'Monday';
      return `Weekly on ${dayName}s at ${timeStr}`;
    }

    case 'weekdays':
      return `Every weekday at ${timeStr}`;

    case 'monthly': {
      const day = parseInt(parsed.dayOfMonth, 10);
      const suffix = getOrdinalSuffix(day);
      return `Monthly on the ${day}${suffix} at ${timeStr}`;
    }

    default:
      return `Daily at ${timeStr}`;
  }
};

/**
 * Helper function to get ordinal suffix for a number
 * @param {number} n - The number to get suffix for
 * @returns {string} Ordinal suffix (st, nd, rd, or th)
 */
const getOrdinalSuffix = n => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

/**
 * Calculates the next run date from an RRULE string
 * @param {string} rruleString - The RRULE string to calculate from
 * @returns {string} Formatted next run date (e.g., "Monday, Dec 10th at 10:00 AM")
 */
export const getNextRunDate = rruleString => {
  if (!rruleString) return '';

  const parsed = parseScheduledPromptRRule(rruleString);
  const [hours, minutes] = parsed.time.split(':').map(Number);
  const now = new Date();
  const nextRun = new Date();

  // Set the time
  nextRun.setHours(hours, minutes, 0, 0);

  // If the time has already passed today, start from tomorrow
  if (nextRun <= now) {
    nextRun.setDate(nextRun.getDate() + 1);
  }

  switch (parsed.frequency) {
    case 'daily':
      // Already set to next occurrence
      break;

    case 'weekly': {
      const dayMap = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };
      const targetDay = dayMap[parsed.dayOfWeek] ?? 1;
      const currentDay = nextRun.getDay();
      let daysUntilTarget = targetDay - currentDay;

      if (daysUntilTarget < 0) {
        daysUntilTarget += 7;
      } else if (daysUntilTarget === 0 && nextRun <= now) {
        daysUntilTarget = 7;
      }

      nextRun.setDate(nextRun.getDate() + daysUntilTarget);
      break;
    }

    case 'weekdays': {
      // Find next weekday (Mon-Fri)
      while (
        nextRun.getDay() === 0 ||
        nextRun.getDay() === 6 ||
        nextRun <= now
      ) {
        nextRun.setDate(nextRun.getDate() + 1);
      }
      break;
    }

    case 'monthly': {
      const targetDayOfMonth = parseInt(parsed.dayOfMonth, 10);
      nextRun.setDate(targetDayOfMonth);

      // If the date has passed this month, go to next month
      if (nextRun <= now) {
        nextRun.setMonth(nextRun.getMonth() + 1);
        nextRun.setDate(targetDayOfMonth);
      }

      // Handle months with fewer days
      const maxDays = new Date(
        nextRun.getFullYear(),
        nextRun.getMonth() + 1,
        0
      ).getDate();

      if (targetDayOfMonth > maxDays) {
        nextRun.setDate(maxDays);
      }
      break;
    }

    default:
      break;
  }

  // Format the date
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];

  const dayOfWeekName = dayNames[nextRun.getDay()];
  const monthName = monthNames[nextRun.getMonth()];
  const dayOfMonth = nextRun.getDate();
  const suffix = getOrdinalSuffix(dayOfMonth);

  // Format time as 12-hour with AM/PM
  let formattedHours = nextRun.getHours();
  const ampm = formattedHours >= 12 ? 'PM' : 'AM';
  formattedHours %= 12;
  formattedHours = formattedHours || 12; // 0 should be 12
  const formattedMinutes = String(nextRun.getMinutes()).padStart(2, '0');

  return `${dayOfWeekName}, ${monthName} ${dayOfMonth}${suffix} at ${formattedHours}:${formattedMinutes} ${ampm}`;
};

/**
 * Converts a category enum value or 'ALL' string to a URL slug
 * @param {EScheduledPromptTypes | string} category - The category to convert
 * @returns {string} The URL slug (e.g., 'my', 'teammates', 'default', 'all')
 */
export const categoryToSlug = category => {
  if (category === 'ALL') {
    return 'all';
  }
  if (category === EScheduledPromptTypes.PRIVATE) {
    return 'my';
  }
  if (category === EScheduledPromptTypes.ORGANIZATION) {
    return 'teammates';
  }
  if (category === EScheduledPromptTypes.DEFAULT) {
    return 'default';
  }
  if (category === SCHEDULED_FOR_YOU) {
    return 'scheduled';
  }
  return 'all';
};

/**
 * Converts a URL slug to a category enum value or 'ALL'
 * @param {string} slug - The URL slug to convert
 * @returns {EScheduledPromptTypes | 'ALL'} The category enum value or 'ALL'
 */
export const slugToCategory = slug => {
  const normalized = (slug || '').toLowerCase();
  switch (normalized) {
    case 'my':
    case 'private': // Keep backward compatibility
      return EScheduledPromptTypes.PRIVATE;
    case 'teammates':
    case 'organization': // Keep backward compatibility
      return EScheduledPromptTypes.ORGANIZATION;
    case 'default':
      return EScheduledPromptTypes.DEFAULT;
    case 'scheduled':
      return SCHEDULED_FOR_YOU;
    case 'all':
    default:
      return 'ALL';
  }
};

// Fallback color combinations for collections
const FALLBACK_COLORS = [
  'bg-purple-50 border-purple-200',
  'bg-indigo-50 border-indigo-200',
  'bg-pink-50 border-pink-200',
  'bg-orange-50 border-orange-200',
  'bg-amber-50 border-amber-200',
  'bg-cyan-50 border-cyan-200',
  'bg-lime-50 border-lime-200',
  'bg-fuchsia-50 border-fuchsia-200',
  'bg-blue-50 border-blue-200'
];

// Map to track assigned colors to collection IDs
const collectionColorMap = new Map();

/**
 * Converts a collection icon to an emoji character
 * Handles both direct emoji strings and Unicode hex code point strings
 * @param {string | undefined} icon - The icon field which can be an emoji character or hex code (e.g., "💼" or "1f4c1")
 * @returns {string | null} The emoji character or null if invalid
 */
export const getCollectionIconEmoji = icon => {
  if (!icon) {
    return null;
  }
  // If it's a hex code point (only contains hex characters and possibly hyphens)
  if (/^[0-9a-f-]+$/i.test(icon)) {
    try {
      const codePoints = icon
        .split('-')
        .map(part => parseInt(part, 16))
        .filter(Number.isFinite);
      return codePoints.length ? String.fromCodePoint(...codePoints) : null;
    } catch (error) {
      return null;
    }
  }
  // Otherwise, assume it's already an emoji character
  return icon;
};

/**
 * Converts an emoji character to a hex code point string
 * Used for emoji picker which expects hex format
 * @param {string | undefined} icon - The icon field which can be an emoji character or hex code
 * @returns {string | null} The hex code point or null if invalid
 */
export const getCollectionIconHexCode = icon => {
  if (!icon) {
    return null;
  }
  // If it's already a hex code, return it
  if (/^[0-9a-f-]+$/i.test(icon)) {
    return icon;
  }
  // Convert emoji character (possibly multi‑codepoint) to hex code point(s)
  try {
    const codePoints = Array.from(icon).map(ch =>
      ch.codePointAt(0)?.toString(16).toLowerCase()
    );
    return codePoints.every(Boolean) ? codePoints.join('-') : null;
  } catch (error) {
    return null;
  }
};

/**
 * Generate a deterministic color class for a collection ID
 * Assigns colors in order of first appearance to avoid repetition when possible
 * @param {string} collectionId - The collection ID to generate a color for
 * @returns {string} Tailwind CSS classes for background and border colors
 */
export const getCollectionColorClass = collectionId => {
  if (!collectionId) {
    return FALLBACK_COLORS[0];
  }

  // Check if we already assigned a color to this collection
  if (collectionColorMap.has(collectionId)) {
    return collectionColorMap.get(collectionId);
  }

  // Assign the next available color based on map size
  const colorIndex = collectionColorMap.size % FALLBACK_COLORS.length;
  const color = FALLBACK_COLORS[colorIndex];

  collectionColorMap.set(collectionId, color);

  return color;
};
